import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Select,
  Stack,
  Text
} from "@chakra-ui/react";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import ListPageCardHeader from "../../../components/Card/ListPageCardHeader";
import Loading from "../../../components/Loading/Loading";
import { MonthlyReportType, ReportStatuses } from "../../../constants/reports";
import { ROUTES } from "../../../constants/routes-path";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import { toServerDate } from "../../../utils/traffic-violation-utils";
import { reportsService } from "services/reportsService";
import { useCustomToast } from "components/CustomToast/CustomToast";
import DateTime from "react-datetime";
import moment from "moment";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";
import { isoToBrFormat } from "utils/utils";

function MonthlyReport() {
  const { type } = useParams();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const [loadingExport, setLoadingExport] = useState(false);

  const typeObj = useMemo(() => {
    return Object.values(MonthlyReportType).find(item => item.url === type);
  }, [type]);

  const ids = useMemo(
    () => ({
      initialDate: "initial-date",
      finalDate: "final-date",
      status: "status",
    }),
    []
  );

  const statusRequiredTypes = [
    MonthlyReportType.QUANTITATIVE_INFRACTIONS_AGENTS_MONTH.url,
    MonthlyReportType.QUANTITATIVE_INFRACTIONS_EQUIPMENT_MONTH.url,
    MonthlyReportType.QUANTITATIVE_INFRACTIONS_TYPES_MONTH.url
  ];

  const firstToToday = [
    MonthlyReportType.QUANTITATIVE_INFRACTIONS_AGENTS_MONTH.url,
    MonthlyReportType.QUANTITATIVE_INFRACTIONS_EQUIPMENT_MONTH.url,
    MonthlyReportType.QUANTITATIVE_INFRACTIONS_TYPES_MONTH.url,
    MonthlyReportType.QUANTITATIVE_PERMITTEES_DOCUMENTS_MONTH.url
  ];

  const previousMonth = [
    MonthlyReportType.SUMMARY_VALUES_INFRACTIONS.url
  ];

  const fullYear = [
    MonthlyReportType.VALUES_INFRACTIONS_TO_BE_RECEIVED_MONTH.url
  ];

  const isStatusRequired = statusRequiredTypes.includes(typeObj?.url);

  const getToday = () => new Date();

  const getFirstDayOfCurrentMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getFirstDayOfPreviousMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1);
  };

  const getLastDayOfPreviousMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 0);
  };

  const getFirstDayOfYear = () => {
    const date = new Date();
    return new Date(date.getFullYear(), 0, 1);
  };

  const getLastDayOfYear = () => {
    const date = new Date();
    return new Date(date.getFullYear(), 11, 31);
  };

  const { initialDateValue, finalDateValue } = useMemo(() => {
    if (!typeObj?.url) {
      return {
        initialDateValue: getToday().toISOString().split("T")[0],
        finalDateValue: getToday().toISOString().split("T")[0]
      };
    }

    if (firstToToday.includes(typeObj.url)) {
      return {
        initialDateValue: getFirstDayOfCurrentMonth().toISOString().split("T")[0],
        finalDateValue: getToday().toISOString().split("T")[0],
      };
    }

    if (previousMonth.includes(typeObj.url)) {
      return {
        initialDateValue: getFirstDayOfPreviousMonth().toISOString().split("T")[0],
        finalDateValue: getLastDayOfPreviousMonth().toISOString().split("T")[0],
      };
    }

    if (fullYear.includes(typeObj.url)) {
      return {
        initialDateValue: getFirstDayOfYear().toISOString().split("T")[0],
        finalDateValue: getLastDayOfYear().toISOString().split("T")[0],
      };
    }

    return {
      initialDateValue: getToday().toISOString().split("T")[0],
      finalDateValue: getToday().toISOString().split("T")[0],
    };
  }, [typeObj]);

  const initialFormState = useMemo(() => {
    const base = {
      [ids.initialDate]: {
        value: moment(initialDateValue, DATE_FORMAT_ISO),
        validations: [{ required: true }],
      },
      [ids.finalDate]: {
        value: moment(finalDateValue, DATE_FORMAT_ISO),
        validations: [{ required: true }],
      },
    };

    if (isStatusRequired) {
      base[ids.status] = {
        value: ReportStatuses.VALIDATED.value,
        validations: [],
      };
    }

    return base;
  }, [ids, initialDateValue, finalDateValue, isStatusRequired]);

  const [formData, setFormData] = useState(initialFormState);
  const { t } = useTranslation();
  const [inputError, setInputError] = useState({});

  const statusList = useMemo(() => [
    ReportStatuses.VALIDATED,
    ReportStatuses.CANCELED,
    ReportStatuses.DIVERGENT,
    ReportStatuses.AGENT_ERROR,
    ReportStatuses.OTHERS,
    ReportStatuses.WARNING,
    ReportStatuses.OTHER_STATES,
    ReportStatuses.NOT_REGISTERED,
    ReportStatuses.NOT_VALIDATED,
    ReportStatuses.UNREADABLE,
    ReportStatuses.DUPLICATED,
    ReportStatuses.ERRO_REG_EQUIP,
    ReportStatuses.VEHICLE_EMERGENCY,
  ], []);

  const reportExportMap = {
    [MonthlyReportType.QUANTITATIVE_INFRACTIONS_AGENTS_MONTH.url]: reportsService.exportQuantitativeInfractionsAgentsMonth,
    [MonthlyReportType.QUANTITATIVE_INFRACTIONS_EQUIPMENT_MONTH.url]: reportsService.exportQuantitativeInfractionsEquipmentMonth,
    [MonthlyReportType.QUANTITATIVE_INFRACTIONS_TYPES_MONTH.url]: reportsService.exportQuantitativeInfractionsTypesMonth,
    [MonthlyReportType.SUMMARY_VALUES_INFRACTIONS.url]: reportsService.exportSummaryValuesInfractions,
    [MonthlyReportType.VALUES_INFRACTIONS_TO_BE_RECEIVED_MONTH.url]: reportsService.exportValuesInfractionsToBeReceivedMonth,
    [MonthlyReportType.QUANTITATIVE_PERMITTEES_DOCUMENTS_MONTH.url]: reportsService.exportMonthlyPermitteeDocuments,
  };

  const handleInputChange = (e) => {
    const id = e.target.id;
    const value = e.target.value;

    const newFormData = {
      ...formData,
      [id]: {
        ...formData[id],
        value,
      },
    };

    setFormData(newFormData);
    setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
  };

  const handleInitialDateChange = (value) => {
    const m = moment(value);
    const updated = {
      ...formData,
      [ids.initialDate]: {
        ...formData[ids.initialDate],
        value: m.isValid() ? m : null,
      },
    };
    setFormData(updated);
    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: ids.initialDate })
    );
  };

  const handleFinalDateChange = (value) => {
    const m = moment(value);
    const updated = {
      ...formData,
      [ids.finalDate]: {
        ...formData[ids.finalDate],
        value: m.isValid() ? m : null,
      },
    };
    setFormData(updated);
    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: ids.finalDate })
    );
  };

  const toDTO = () => ({
    ...(isStatusRequired && { status: formData[ids.status].value }),
    data_inicial: isoToBrFormat(formData[ids.initialDate].value?.format(DATE_FORMAT_ISO)),
    data_final: isoToBrFormat(formData[ids.finalDate].value?.format(DATE_FORMAT_ISO)),
  });

  const handleClearForm = async () => {
    setFormData(initialFormState);
    setInputError({});
  }

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const dto = toDTO();
      const exportFn = reportExportMap[typeObj?.url];
      const response = await exportFn(dto);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${typeObj.url}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setLoadingExport(false);
    }
  };

  const isFormInvalid = formData && inputError
    ? hasError({ formData, inputError })
    : true;

  return (
    <>
      {loadingExport && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(255, 255, 255, 0.6)"
          display="flex"
          justifyContent="center"
          alignItems="center"
          zIndex="overlay"
        >
          <Loading maxH={{ sm: "100px", md: "350px" }} />
        </Box>
      )}

      <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "90px" }}>
        <Card>
          <ListPageCardHeader
            title={t(typeObj?.title)}
            subtitle={t(typeObj?.description)}
          />
          <CardBody px="22px">
            {formData ? (
              <>
                <Grid gap="30px" templateColumns={{ sm: "1fr", md: "repeat(3, 1fr)" }}>
                  {/*data_inicial*/}
                  <FormControl isRequired isInvalid={inputError[ids.initialDate]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`reports.monthly-report.fields.initial-date`)}
                    </FormLabel>
                    <DateTime
                      dateFormat={DATE_FORMAT}
                      timeFormat={false}
                      locale={LOCALE_PT_BR}
                      value={
                        formData[ids.initialDate].value
                          ? moment(formData[ids.initialDate].value, DATE_FORMAT_ISO)
                          : ""
                      }
                      renderInput={(props) => (
                        <Input
                          {...props}
                          fontSize="sm"
                          placeholder={t("system.placeholders.date")}
                          variant="main"
                        />
                      )}
                      onChange={handleInitialDateChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.initialDate])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*data_final*/}
                  <FormControl isRequired isInvalid={inputError[ids.finalDate]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`reports.monthly-report.fields.final-date`)}
                    </FormLabel>
                    <DateTime
                      dateFormat={DATE_FORMAT}
                      timeFormat={false}
                      locale={LOCALE_PT_BR}
                      value={
                        formData[ids.finalDate].value
                          ? moment(formData[ids.finalDate].value, DATE_FORMAT_ISO)
                          : ""
                      }
                      renderInput={(props) => (
                        <Input
                          {...props}
                          fontSize="sm"
                          placeholder={t("system.placeholders.date")}
                          variant="main"
                        />
                      )}
                      onChange={handleFinalDateChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.finalDate])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*status*/}
                  {isStatusRequired && (
                    <FormControl>
                      <FormLabel fontSize="xs" fontWeight="bold">
                        {t("reports.monthly-report.fields.status")}
                      </FormLabel>
                      <Select
                        fontSize="sm"
                        id={ids.status}
                        value={formData[ids.status]?.value}
                        onChange={handleInputChange}
                      >
                        <option value={null}></option>
                        {statusList?.map((option, index) => (
                          <option key={index} value={option.value}>
                            {t(`reports.monthly-report.status-options.${option.labelKey}`)}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              </>
            ) : (
              <Loading h="300px" w="100%" />
            )}
          </CardBody>

          <Flex w="100%" direction="row" justify="space-between" align="center" mt="20px">
            <Button
              variant="light"
              fontSize="sm"
              w="125px"
              onClick={() => navigate(ROUTES.reportsPath)}
            >
              {t("system.actions.back")}
            </Button>

            <Stack w="100%" direction="row" justify="end">
              <Button
                w="125px"
                fontSize="sm"
                variant="outline"
                colorScheme="light"
                onClick={handleClearForm}
                _hover={isFormInvalid ? {} : undefined}
              >
                <Text>
                  {t("system.actions.clear")}
                </Text>
              </Button>

              <Button
                w="125px"
                fontSize="sm"
                colorScheme="blue"
                isDisabled={isFormInvalid}
                onClick={handleExport}
                _hover={isFormInvalid ? {} : undefined}
              >
                <Text>
                  {t("system.actions.export")}
                </Text>
              </Button>
            </Stack>
          </Flex>
        </Card>
      </Flex>
    </>
  )
}

export default MonthlyReport;